import React from "react";

export enum PRODUCT_IDS {
    RAW_FIBER = 1000,
    STATIC_IP = 1003,
    DYNAMIC_IP = 1004,
    EXTENDER = 3000,
    FBSA = 1005,
}

export enum PRODUCT_TECHNOLOGY {
    DSL = 'Dsl',
    COAX = 'Coax',
    FIBER = 'Fiber',
}

export enum CUSTOMER_TYPES {
    END_CUSTOMER_LARGE_ACCOUNT = 39,
    END_CUSTOMER_GROSS_SALARY = 40,
}

export enum CUSTOMER_STATUS {
    ACTIVE     = 'ACTIVE',
    ORDER_UP   = 'ORDER_UP',
    ORDER_DOWN = 'ORDER_DOWN',
    FINISH     = 'FINISH',
    CANCEL     = 'CANCEL',
    UNDEFINED  = 'UNDEFINED',
}

export enum PRODUCT_STATUS {
    PENDING = 'PENDING',
    ACTIVE  = 'ACTIVE',
    FINISH  = 'FINISH',
    CANCEL  = 'CANCEL',
    SUSPEND = 'SUSPEND',
}

export enum ERROR_REPORT_STATUS {
    ACTIVE = 'ACTIVE',
    PENDING = 'PENDING',
    PENDING_MAX = 'PENDING_MAX',
    SOFTCLOSED = 'SOFTCLOSED',
    NONE = 'NONE',
    EXCEPTION = 'EXCEPTION',
}

export enum SELFCARE_REPORT_FLOW_TYPE {
    OFFLINE  = 'OFFLINE',
    UNSTABLE = 'UNSTABLE',
    SPEED    = 'SPEED',
}

export interface ErrorReportingStatus {
    error_report_status: ERROR_REPORT_STATUS,
    product_instance_date: string | null,
    rate_limited_product_instance_id: number[],
}

export interface CustomerType {
    customer_type_id: number,
    customer_type: string,
    customer_type_display_name: string,
    cancel_limit_days: number,
    parent_company_name: string,
}

export interface PaymentMethod {
    payment_method_id: number,
    payment_method: string,
    payment_method_display_name: string
}

export interface Customer {
    customer_id: string,
    customer_type: CustomerType,
    customer_contact: Contact,
    customer_permission: PermissionOption[],
    customer_permission_text_accepted: string,
    payment_method: PaymentMethod,
    product_instances: ProductInstance[],
    customer_contact_change: ContactChange,
    customer_context: CustomerContext,
}

export interface ContactPermission {
    contact_email_permission: boolean,
    contact_sms_permission: boolean,
    contact_phone_permission: boolean,
    contact_permission_updated_at: string,
    contact_nps_permission: boolean,
}

export interface PermissionOption {
    is_action_set: boolean,
    permission_type_name: string,
    permission_type_id: number,
}

export interface StaticNotificationType {
    title: (payload: {[key: string]: string|number}) => React.JSX.Element | string
    important: boolean
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any
    className?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    link: any,
    element?: (payload: {[key: string]: string|number}) => React.JSX.Element
}


export interface StaticNotification {
    type: string,
    payload: {[key: string]: string|number},
}

export interface CustomerContext {
    can_request_sbbu: boolean,
    notifications: StaticNotification[],
    finances: {
        balance: number,
        has_card: boolean,
        card: Card | null,
        is_paying_overdue_invoices: boolean,
    }
    customer_status: CUSTOMER_STATUS
}

export interface Card {
    brand: string,
    last_four_digits: string,
    expiration: string,
    expires_soon: boolean,
}

export interface Invoice {
    invoice_id: number,
    type: string,
    sum: string,
    status: string,
    due_date: string | null,
}

export interface InvoiceLine {
    invoice_display_name: string
    amount_base: string | null
    amount_vat: string | null
    amount_total: string | null
    period_start: string
    period_end: string
}

export interface NextInvoice extends Invoice {
    invoice_lines: InvoiceLine[],
}

export type CommunicationRequestId = number;

export interface Communication {
    request_id: CommunicationRequestId,
    channel: CommunicationChannel,
    subject: string,
    sent_dtm: string,
    body: string,
    attachments: Attachment[],
    read_dtm: string | null,
    slug: string,
    icon: string,
    important: boolean,
}

export enum CommunicationChannel {
    LETTER = "LETTER",
    EMAIL = "EMAIL",
    SMS = "SMS",
    EXCEL = "EXCEL",
    NOTIFICATION = "NOTIFICATION",
}

export interface Hardware {
    hardware_inventory_id: number,
    hardware_type_id: number,
    hardware_display_name: string,
    wifi_generation: string,
    wifi_antenna_configuration: string,
    wifi_standard: string,
    wifi_frequency_configuration: string,
    lan_port_configuration: string,
    wan_port_configuration: string,
    wifi_mesh_compatible: boolean,
    image_selfcare: string,
    slug: string,
    serial_number: string,
}

export interface Pagination {
    current_page: number,
    total_pages: number,
    total_records: number,
    records_per_page: number,
}

export interface Attachment {
    request_id: number,
    file_uri: string,
    external_filename: string,
}

export interface Contact {
    contact_type: string,
    first_name: string,
    last_name: string,
    co_name: string,
    company_name: string,
    phone_no: string,
    mobile_no: string,
    email: string,
    cvr: number,
    ean: number,
    contact_mute: boolean,
    street_name: string,
    street_number: number,
    street_letter: string,
    street_floor: string,
    street_side: string,
    street_zip: string,
    city: string,
    municipal_code: number,
    street_code: number
}

export interface DiscountInstance {
    discount_instance_invoice_line_display: string,
    discount_instance_amount_base: number|null,
    discount_instance_amount_vat: number|null,
    discount_instance_percentage: number|null,
}

export interface DiscountInstanceMonetary extends DiscountInstance {
    discount_instance_amount_base: number,
    discount_instance_amount_vat: number,
    discount_instance_percentage: null,
}

export interface DiscountInstancePercentage extends DiscountInstance {
    discount_instance_amount_base: null,
    discount_instance_amount_vat: null,
    discount_instance_percentage: number,
}

export interface ChargePeriod {
    product_price_id: number,
    product_id: number,
    tariff_id: number,
    product_price_status_active_date: string,
    product_price_status_finish_date: string,
    amount_base: number,
    amount_vat: number,
    amount_total: number,
    product_price_status: string,
    discount_instances: DiscountInstance[],
    start_date: string,
    end_date: string,
}

export interface ProductInstance {
    product_instance_id: number,
    parent_product_instance_id: number,
    parent_product_id: number,
    product: {
        product_id: number,
        product_name: string,
        product_display_name: string,
        product_invoice_display_name: string,
        att_statistics_technology: string | null,
        product_attribute_main: boolean,
    },
    product_instance_status_pending_date: string | null,
    product_instance_status_active_date: string | null,
    product_instance_status_finish_date: string | null,
    product_instance_status_cancel_date: string | null,
    product_instance_status_suspend_date: string | null,
    product_instance_mandatory_limit_date: string | null,
    product_instance_status_cancel_limit_date: string | null,
    product_instance_status_pending_finish_date: string | null,
    product_instance_status: PRODUCT_STATUS,
    product_instance_offline_date: string | null,
    amount_total: string | null,
    circuit: Circuit | null,
    lan_port: number | null,
    charge_periods: ChargePeriod[],
    is_softclosed: boolean,
    can_see_ont_internet_status: boolean,
    can_see_network_overview: boolean,
}

export interface ContactChange {
    mobile_new: string,
    mobile_old: string,
    email_new: string,
    email_old: string,
    retries_left: number,
}

export interface PaymentFormData {
    version: string
    description: string
    merchant_id: number
    agreement_id: number
    order_id: number
    amount: number
    payment_methods: string
    language: string
    type: string
    currency: string
    continueurl: string
    cancelurl: string
    checksum: string
    quickpay_post_action: string
}

export interface Circuit {
    account_no: string | null,
    activation_code: string | null,
}

export interface Competitor {
    competitor_id: number,
    competitor_display_name: string,
    competitor_help_text: string,
    competitor_custom_provider: boolean,
    competitor_show_in_dsl_sbbu: boolean,
    competitor_sbbu_name: string,
    competitor_show_in_sbbu: boolean,
}

export interface LoadingProps {
    width?: string,
    height?: string,
}

export interface DeliveryFlowOverview {
    workflow_instance_id: number,
}

export interface DefaultDeliveryFlow {
    timeline_info: DefaultTimelineInfo,
    workflow_events: DefaultWorkflowEvent[],
    timeline_metadata: DefaultTimelineMetadata[],
    communication_requests: Communication[]
}

export interface DefaultTimelineInfo {
    timeline_type: string,
}

export interface DefaultWorkflowEvent {
    workflow_event_id: number,
    workflow_event_type: string,
    workflow_event_payload: WorkflowEventPayload[],
    workflow_event_dtm: string,
}

export interface ComponentSubmitHandler {
    hasErrors(flowData: FlowData): boolean;
    scrollToError(): void;
}

export type RegisterFunction = (component_name: string, component: ComponentSubmitHandler) => void;

export interface WorkflowEventPayload {
    key: string,
    value: string,
}

export interface DefaultTimelineMetadata {
    key: string,
    value: string,
}

export interface CpeMetadata {
    ping4: 'Yes' | 'No',
    ping6: 'Yes' | 'No',
    result: 'SUCCESS' | 'FAIL',
}

export interface OntMetadata {
    loss_of_signal: 'Yes' | 'No',
    dying_gasp: 'Yes' | 'No',
    link_state: 'Up' | 'Down',
    mac_address: string,
}

export interface MacMetadata {
    mac_address: string,
}

export type StatusTypes = 'green' | 'red' | 'black';

export const STATUS_TYPES = {
    green: 'green' as StatusTypes,
    red: 'red' as StatusTypes,
    black: 'black' as StatusTypes,
} as const;

export type CpeStatusTypes = 'fail' | 'down' | 'noMac' | 'up';

export const CPE_STATUS_TYPES = {
    fail: 'fail' as CpeStatusTypes,
    down: 'down' as CpeStatusTypes,
    noMac: 'noMac' as CpeStatusTypes,
    up: 'up' as CpeStatusTypes,
} as const;

export type OntStatusTypes = 'fail' | 'down' | 'up';

export const ONT_STATUS_TYPES = {
    fail: 'fail' as OntStatusTypes,
    down: 'down' as OntStatusTypes,
    up: 'up' as OntStatusTypes,
} as const;

export interface WifiMetadata {
    macs: Record<string, WifiEntity>;
    wlans: Record<string, WlanEntity>;
    router: RouterEntity
    result: string;
}

export interface WifiEntity {
    ago: string;
    band: string;
    last: string;
    name: string;
    rssi: number;
    vendor: string;
}

export interface WlanEntity {
    band: string;
    bandwidth: string;
    channel: string;
    ifid: string;
    mpro: string;
    ssid: string;
    updated: string;
}

export interface RouterEntity {
    serial?: string;
}

export interface OptInMetadata {
    id: number | null,
    confirmed: boolean,
}

export type FlowData = {
    flowtype: SELFCARE_REPORT_FLOW_TYPE,
    technology?: string | null,
    devices?: string[];
    otherDeviceDescription?: string;
    email: string;
    phone: string;
    selectedContact: {
        email: boolean;
        phone: boolean;
    };
    editingContact: {
        email: boolean;
        phone: boolean;
    };
    fiberBoxLights?: boolean | undefined;
    gponSerial?: string,
    isCableConnected?: boolean | null,
    descriptionText: string,
    ownRouter?: boolean | null,
    ownRouterLights?: string,
    ownHardware?: boolean | null,
    ownHardwareDescription?: string,
    coaxPlugType?: string,
    connectionType?: string,
    attachedFiles?: File[],
};

